"use client"

import { Loading } from "@/app/components/Loading"
import { Button } from "@/components/ui/button"
import { subscribeUserToBlogViaFrame } from "@/lib/api/farcaster"

import sdk from "@farcaster/frame-sdk"

import { Check } from "lucide-react"
import { useEffect, useState } from "react"

import Farcaster from "public/publication/farcaster.png"
import Image from "next/image"

import { toast } from "sonner"
import { User } from "@/types/users"
import { useRouter } from "next/navigation"

/**
 * Separate flow, for in-warpcast notifications.
 *
 */
export default function FarcasterSubscribe({
  secondary,
  className,
  children,
  blogSlug,
  user,
  isSubscribed,
}: {
  secondary?: boolean
  className?: string
  children: React.ReactNode
  blogSlug: string
  user?: User
  isSubscribed?: boolean
}) {
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState<undefined | boolean>()
  const router = useRouter()

  console.log("Is user subscribed?", isSubscribed)

  const handleSubscribe = async () => {
    try {
      setLoading(true)
      // User needs to first add frame; this is a no-op if they already have it added.
      await sdk.actions.addFrame()

      if (!user?.lowercase_wallet_address)
        throw new Error("User trying to subscribe but no wallet address.")

      const response = await subscribeUserToBlogViaFrame({
        lowercaseBlogUrl: blogSlug.toLowerCase(),
      })

      if (response && "subscribed" in response) {
        setSuccess(true)

        // Refresh, to properly show state
        // on other subscribe buttons.
        router.refresh()
      } else {
        setSuccess(false)
      }

      setLoading(false)
    } catch (e) {
      console.error("Error subscribing user to blog:", e)
      setLoading(false)
    }
  }

  useEffect(() => {
    if (success === undefined) return

    if (success) {
      toast("Subscribed! You'll receive notifications directly in Warpcast.")
    } else {
      toast("Something went wrong. Let us know by casting in /paragraph.")
    }
  }, [success])

  // Don't show button if user is not logged in which should be automatic via Privy
  if (!user) return null

  let text

  if (loading) {
    text = <Loading className="h-6 w-6" />
  } else if (success) {
    text = <Check className="text-primary" />
  } else if (isSubscribed) {
    text = (
      <>
        <Image
          src={Farcaster}
          alt="Farcaster"
          className="h-4 w-4 inline-block mr-2"
        />
        Subscribed
      </>
    )
  } else {
    text = (
      <>
        <Image
          src={Farcaster}
          alt="Farcaster"
          className="h-4 w-4 inline-block mr-2"
        />
        Subscribe
      </>
    )
  }

  return (
    <Button
      variant={secondary ? "secondary" : undefined}
      className={className}
      onClick={handleSubscribe}
      disabled={isSubscribed || loading || success}
    >
      {text}
    </Button>
  )
}
